import { useCallback, useEffect, useState } from 'react';
import { useSystem } from '@ohif/core';
import {
  ReportAssessmentService,
  TrackedAssessment,
  hasSubmittableRows,
  isNoFindings,
} from '../services/ReportAssessmentService';

/**
 * The dialog's rows, read through to the service (D13).
 *
 * develop's version took an `open` flag and lazily fetched on first open, guarded by
 * TWO refs — `initializedRef`, which it wrote and never read, and `hasSetInitialStateRef`,
 * which did the work (§6 defect 1). Neither is needed: REQUIRES_ASSESSMENT is reachable
 * only after DiagnosisService.refresh() has already called
 * reportAssessmentService.refreshStudy(), so the rows are in the service before the
 * dialog can exist.
 *
 * develop's cleanup also called `unsubscribe()` with NO argument, so its own widened
 * unsubscribe never evicted anything (§6 defect 2). There is no widened unsubscribe
 * here; eviction is onModeExit on both services, driven by the mode lifecycle.
 */
export function useReportAssessment(
  studyInstanceUid: string,
  onClose: () => void
): {
  rows: readonly TrackedAssessment[];
  isNoFindings: boolean;
  isSubmitDisabled: boolean;
  /** Both take the row's index in `rows`, which is the service's row identity. */
  setClassification: (index: number, value?: boolean) => void;
  setDetection: (index: number, value?: boolean) => void;
  submit: () => Promise<void>;
} {
  const { servicesManager } = useSystem();
  const { reportAssessmentService, diagnosisService } = servicesManager.services;

  const [rows, setRows] = useState<readonly TrackedAssessment[]>(
    () => reportAssessmentService.getStudy(studyInstanceUid).assessments
  );

  useEffect(() => {
    const read = ({ studyInstanceUid: changed }: { studyInstanceUid: string }) => {
      if (changed === studyInstanceUid) {
        setRows(reportAssessmentService.getStudy(studyInstanceUid).assessments);
      }
    };

    const subscriptions = [
      reportAssessmentService.subscribe(ReportAssessmentService.EVENTS.ASSESSMENT_UPDATED, read),
      reportAssessmentService.subscribe(ReportAssessmentService.EVENTS.ASSESSMENT_SUBMITTED, read),
    ];

    setRows(reportAssessmentService.getStudy(studyInstanceUid).assessments);

    return () => subscriptions.forEach(subscription => subscription.unsubscribe());
  }, [reportAssessmentService, studyInstanceUid]);

  /**
   * Two separate callbacks rather than one memoised factory. The factory was a
   * useCallback returning a function and was INVOKED during render, so what reached the
   * toggles was a fresh identity every render and the memo bought nothing — it memoised
   * the factory, not its products. One duplicated line is the price of that not
   * recurring.
   */
  const setClassification = useCallback(
    (index: number, value?: boolean) =>
      reportAssessmentService.setVerdict(
        studyInstanceUid,
        index,
        'isClassificationCorrect',
        value
      ),
    [reportAssessmentService, studyInstanceUid]
  );

  const setDetection = useCallback(
    (index: number, value?: boolean) =>
      reportAssessmentService.setVerdict(studyInstanceUid, index, 'isDetectionCorrect', value),
    [reportAssessmentService, studyInstanceUid]
  );

  return {
    rows,

    /**
     * Both derived from `rows` rather than read off the service inside the render body,
     * so the two can never disagree with `rows` within a single render — the bug they
     * replace was that tear, not staleness: the old direct read went straight into a Map
     * this hook does not observe, and while an unrelated re-render picked up the Map's
     * latest value on its own, a render that also used `rows` for the row list could see
     * one answer from the live Map and a stale one from `rows` at once.
     *
     * That consistency does not make `rows` itself current. `rows` only updates on
     * ASSESSMENT_UPDATED/ASSESSMENT_SUBMITTED, and
     * ReportAssessmentService.refreshStudy — called from DiagnosisService's poll/read
     * path, not from this dialog — mutates the service's Map and returns without
     * broadcasting either. A server-side refresh landing between this hook's renders is
     * invisible here until one of those two events next fires.
     */
    isNoFindings: isNoFindings(rows),
    isSubmitDisabled: !hasSubmittableRows(rows),

    setClassification,
    setDetection,

    // Through DiagnosisService, not ReportAssessmentService, so the optimistic PROCESSING
    // lands in the state everything else reads (§5.6). submitAssessment never rejects for
    // any error it originates -- a throwing DIAGNOSIS_STATE_CHANGED subscriber could still
    // reject it through _broadcastEvent, see DiagnosisService.ts's refresh().
    submit: useCallback(async () => {
      await diagnosisService.submitAssessment(studyInstanceUid);
      onClose();
    }, [diagnosisService, studyInstanceUid, onClose]),
  };
}
